import { emit } from './client.js';
import { getCurrentTraceId } from './trace.js';
import { normalizeName, safeUUID } from './util.js';

// Re-export so self-managed flows (e.g. waReview) can mint their own ids
// without importing util.js directly.
export { safeUUID };

let currentFlow = null; // { flow, flow_id }

/** Begin a multi-step flow; returns the flow_id that threads its steps. */
export function startFlow(flow) {
  try {
    const nf = normalizeName(flow);
    currentFlow = { flow: nf || String(flow).toLowerCase(), flow_id: safeUUID() };
    return currentFlow.flow_id;
  } catch {
    return null; // telemetry must never break the caller's business flow
  }
}
export function endFlow() { currentFlow = null; }
export function currentFlowId() { return currentFlow ? currentFlow.flow_id : null; }

/**
 * Record a funnel step. name = `<flow>_<step>` normalized to the server allow-list
 * (^[a-z0-9_]{1,64}$). Carries flow_id when an active flow matches. Exposed app-wide
 * as $track / window.$track, so the body is wrapped — a throw must never reach the
 * caller (telemetry cardinal rule).
 *
 * When `flowId` is provided it is used as props.flow_id DIRECTLY, bypassing the
 * global currentFlow slot. This allows self-managed flows (e.g. waReview) to emit
 * correlated steps without touching or being affected by the global slot.
 */
export function track(flow, step, outcome = null, extra = {}, flowId = undefined) {
  try {
    // The `<flow>_<step>` join always yields a non-empty [a-z0-9_] string, so
    // normalizeName never returns null here in practice; the guard is defensive.
    const name = normalizeName(`${flow}_${step}`);
    if (!name) return false;
    const nf = normalizeName(flow);
    const props = { flow: nf || String(flow).toLowerCase() };
    if (flowId !== undefined) {
      // Explicit flow_id: bypass the global slot entirely.
      props.flow_id = flowId;
    } else if (currentFlow && nf && currentFlow.flow === nf) {
      props.flow_id = currentFlow.flow_id;
    }
    if (outcome != null) props.outcome = String(outcome).toLowerCase().slice(0, 64);
    Object.assign(props, extra);
    return emit({ event_type: 'funnel', name, trace_id: getCurrentTraceId(), props });
  } catch {
    return false;
  }
}

export function _resetForTest() { currentFlow = null; }
